// @story HIR-HR4
// InterviewSlotsPanel — the times for one interview round, and who has each.
//
// HR adds slots (date, time, length, panel, in person at a place or online at a
// link), then gives a candidate a slot. Giving a slot sends the round's
// invitation through Messages, with a link the candidate can use ONCE to move
// to another open time. A reminder goes before the slot (hours set per school).
//
// If the round's messages are not ready (no template, or one Messages does not
// have) this says so and nobody can be given a slot — nothing is sent instead
// of something wrong.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CalendarClock, MapPin, Video, Users, Plus, RefreshCw, Trash2, Send, AlertTriangle, CheckCircle, X, Bell } from 'lucide-react';
import api from '../../services/api';

const fmt = (d) => new Date(d).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
});
const fmtTime = (d) => new Date(d).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit' });

/* "2026-10-08" + "10:30" typed in India → the instant, whatever the browser's own zone. */
const istInstant = (date, time) => new Date(`${date}T${time}:00+05:30`);

const BLANK = { date: '', time: '10:00', duration: 30, count: 1, mode: 'in_person', location: '', link: '', panel: '' };

function Readiness({ messages }) {
  if (!messages) return null;
  const rows = [
    ['invite', 'Invitation (this round\'s template, set in the pipeline builder)'],
    ['reminder', 'Reminder before the interview (Messages → Communications)'],
    ['rescheduled', '"Interview moved" confirmation (Messages → Communications)'],
  ];
  const allReady = rows.every(([k]) => messages[k]?.ready);
  return (
    <div className={`rounded-xl border p-3.5 ${allReady ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50'}`}>
      <div className={`text-sm font-semibold mb-1.5 ${allReady ? 'text-emerald-700' : 'text-amber-800'}`}>
        {allReady ? 'Messages for this round are ready' : 'Some messages for this round are not ready'}
      </div>
      <ul className="space-y-1">
        {rows.map(([k, label]) => {
          const m = messages[k] || {};
          return (
            <li key={k} className="text-xs flex items-start gap-1.5">
              {m.ready
                ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                : <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />}
              <span className={m.ready ? 'text-slate-600' : 'text-amber-800'}>
                {label}{m.ready ? ` — email "${m.email}"${m.whatsapp ? `, WhatsApp "${m.whatsapp}"` : ', no WhatsApp yet'}` : ` — ${m.reason || 'not set'}`}
              </span>
            </li>
          );
        })}
      </ul>
      {!messages.invite?.ready && (
        <p className="text-xs text-amber-800 mt-2 font-medium">Until the invitation is ready, nobody can be given a slot — so no candidate gets a wrong or empty message.</p>
      )}
      {messages.templatesReadable === false && (
        <p className="text-[11px] text-slate-500 mt-1">Could not read the Messages list just now, so only "is a template set" was checked.</p>
      )}
    </div>
  );
}

export default function InterviewSlotsPanel({ jobId, stage, candidates = [], onChanged }) {
  const [slots, setSlots] = useState([]);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [pick, setPick] = useState({});      // slotId → resumeId chosen in its dropdown
  const [people, setPeople] = useState([]);
  const [hours, setHours] = useState(24);
  const [hoursDraft, setHoursDraft] = useState('24');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/interview-slots/${jobId}/${stage.stageId}`);
      setSlots(r.data?.data?.slots || []);
      setMessages(r.data?.data?.messages || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load the interview slots');
    } finally { setLoading(false); }
  }, [jobId, stage.stageId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/interview-slots/people').then((r) => setPeople(r.data?.data || [])).catch(() => {});
    api.get('/interview-slots/settings').then((r) => {
      const h = r.data?.data?.reminderHoursBefore ?? 24;
      setHours(h); setHoursDraft(String(h));
    }).catch(() => {});
  }, []);

  const holding = useMemo(() => new Set(slots.filter((s) => s.resumeId).map((s) => String(s.resumeId))), [slots]);
  const free = candidates.filter((c) => c.eligible && !holding.has(String(c._id)) && !c.stageResult?.hrDecision);
  const inviteReady = Boolean(messages?.invite?.ready);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const createSlots = async () => {
    if (!form.date || !form.time) { toast.error('Choose a date and a start time'); return; }
    const panel = form.panel.split(',').map((n) => n.trim()).filter(Boolean).map((name) => {
      const match = people.find((p) => p.name.toLowerCase() === name.toLowerCase());
      return match ? { userId: match.userId, name: match.name } : { name };
    });
    const start = istInstant(form.date, form.time);
    const count = Math.max(1, Math.min(20, Number(form.count) || 1));
    const mins = Math.max(5, Number(form.duration) || 30);
    const list = Array.from({ length: count }, (_, i) => ({
      startsAt: new Date(start.getTime() + i * mins * 60000).toISOString(),
      durationMinutes: mins, mode: form.mode, location: form.location, link: form.link, panel,
    }));
    setSaving(true);
    try {
      await api.post(`/interview-slots/${jobId}/${stage.stageId}`, { slots: list });
      toast.success(count > 1 ? `${count} slots added` : 'Slot added');
      setForm((f) => ({ ...BLANK, mode: f.mode, location: f.location, link: f.link, panel: f.panel, duration: f.duration }));
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not add the slots');
    } finally { setSaving(false); }
  };

  const act = async (slotId, fn, ok) => {
    setBusy(slotId);
    try {
      const r = await fn();
      const m = r?.data?.data?.messaged;
      if (m && !m.email && !m.whatsapp) {
        toast.warn(`Done, but the message did not go: ${(m.errors || []).join('; ') || 'no email or WhatsApp on record'}. Use "Send again".`);
      } else if (ok) toast.success(ok);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'That did not work — try again');
    } finally { setBusy(null); }
  };

  const assign = (s) => {
    const resumeId = pick[s._id];
    if (!resumeId) { toast.error('Choose a candidate for this slot'); return; }
    act(s._id, () => api.post(`/interview-slots/slot/${s._id}/assign`, { resumeId }), 'Slot given — invitation sent');
  };

  const saveHours = async () => {
    try {
      const r = await api.put('/interview-slots/settings', { reminderHoursBefore: Number(hoursDraft) });
      setHours(r.data?.data?.reminderHoursBefore);
      toast.success('Reminder time saved for every interview at this school');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };

  if (loading) return <div className="text-sm text-slate-400 py-4">Loading interview slots…</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-orange-500" /> Interview slots
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500 flex items-center gap-1.5">
            <Bell className="w-3.5 h-3.5" /> Reminder
            <input type="number" min={1} max={168} value={hoursDraft} onChange={(e) => setHoursDraft(e.target.value)}
              className="w-14 px-1.5 py-1 border border-slate-200 rounded text-xs text-center" />
            hours before
          </label>
          {String(hours) !== hoursDraft && (
            <button onClick={saveHours} className="text-xs px-2 py-1 rounded bg-slate-800 text-white">Save</button>
          )}
          <button onClick={() => setShowForm((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white rounded-full text-sm font-medium hover:bg-orange-600">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showForm ? 'Close' : 'Add slots'}
          </button>
        </div>
      </div>

      <Readiness messages={messages} />

      {showForm && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="text-xs text-slate-500">Date
              <input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs text-slate-500">Starts (India time)
              <input type="time" value={form.time} onChange={(e) => set({ time: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs text-slate-500">Minutes each
              <input type="number" min={5} max={480} value={form.duration} onChange={(e) => set({ duration: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs text-slate-500">How many, back to back
              <input type="number" min={1} max={20} value={form.count} onChange={(e) => set({ count: e.target.value })} className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
          </div>
          <div className="flex gap-2">
            {[['in_person', 'In person', MapPin], ['online', 'Online', Video]].map(([v, label, Icon]) => (
              <button key={v} onClick={() => set({ mode: v })}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${form.mode === v ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600'}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          {form.mode === 'in_person' ? (
            <label className="text-xs text-slate-500 block">Where
              <input value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. Admin block, Room 4"
                className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
          ) : (
            <label className="text-xs text-slate-500 block">Online link
              <input value={form.link} onChange={(e) => set({ link: e.target.value })} placeholder="https://meet.google.com/…"
                className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
          )}
          <label className="text-xs text-slate-500 block">Panel (names, separated by commas)
            <input value={form.panel} onChange={(e) => set({ panel: e.target.value })} list={`panel-people-${stage.stageId}`}
              placeholder="e.g. Ms Mehta, Mr Rao" className="mt-0.5 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
            <datalist id={`panel-people-${stage.stageId}`}>
              {people.map((p) => <option key={p.userId} value={p.name} />)}
            </datalist>
          </label>
          <button onClick={createSlots} disabled={saving}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50">
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}

      {slots.length === 0 ? (
        <p className="text-sm text-slate-400">No slots yet for this round. Add the times the panel is free.</p>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {slots.map((s) => {
            const past = new Date(s.startsAt).getTime() < Date.now();
            return (
              <div key={s._id} className={`p-3 flex flex-wrap items-center gap-3 ${past ? 'opacity-50' : ''}`}>
                <div className="min-w-[180px]">
                  <div className="text-sm font-medium text-slate-800">{fmt(s.startsAt)} – {fmtTime(s.endsAt)}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    {s.mode === 'online' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                    {s.mode === 'online' ? 'Online' : s.location}
                  </div>
                  {s.panel?.length > 0 && (
                    <div className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> {s.panel.map((p) => p.name).join(', ')}</div>
                  )}
                </div>
                <div className="flex-1 flex flex-wrap items-center gap-2 justify-end">
                  {s.resumeId ? (
                    <>
                      <span className="text-sm text-slate-700 font-medium">{s.candidate?.name || 'Candidate'}</span>
                      {s.reminderSentAt && <span className="text-[11px] text-emerald-700">reminded</span>}
                      <button disabled={busy === s._id || !inviteReady} onClick={() => act(s._id, () => api.post(`/interview-slots/slot/${s._id}/resend`), 'Invitation sent again')}
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                        <Send className="w-3 h-3" /> Send again
                      </button>
                      <button disabled={busy === s._id} onClick={() => {
                        if (!window.confirm('Free this slot? No message is sent — tell the candidate, or give them another slot (which does send).')) return;
                        act(s._id, () => api.post(`/interview-slots/slot/${s._id}/unassign`), 'Slot freed');
                      }}
                        className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                        Free slot
                      </button>
                    </>
                  ) : past ? (
                    <span className="text-xs text-slate-400">Passed</span>
                  ) : (
                    <>
                      <select value={pick[s._id] || ''} onChange={(e) => setPick((p) => ({ ...p, [s._id]: e.target.value }))}
                        className="text-sm border border-slate-200 rounded-lg px-2 py-1 max-w-[220px]">
                        <option value="">Choose a candidate…</option>
                        {free.map((c) => <option key={c._id} value={c._id}>{c.candidateName || c.email}</option>)}
                      </select>
                      <button disabled={busy === s._id || !inviteReady || !pick[s._id]} onClick={() => assign(s)}
                        title={inviteReady ? '' : 'The invitation for this round is not ready'}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-40">
                        {busy === s._id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Give slot
                      </button>
                      <button disabled={busy === s._id} onClick={() => act(s._id, () => api.delete(`/interview-slots/slot/${s._id}`), 'Slot removed')}
                        className="text-slate-300 hover:text-red-500" title="Remove this open slot"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
